import React, { useMemo, useEffect } from "react";
import { Field, change } from "redux-form";
import Utils from "../../../helpers/Utils";
import { useSelector, useDispatch } from "react-redux";
import { SelectPicker } from "../../select-picker/SelectPicker";

const PatientMedicalAlertSubCategories = ({ element, values }) => {
  const dispatch = useDispatch();
  const patientMedicalAlertsSubCategories = useSelector(
    (store) => store.patientRegister.patientMedicalAlertsSubCategories,
  );

  const smokingStatusOptions = useMemo(
    () => Utils.mapOptions(patientMedicalAlertsSubCategories?.smokingStatuses),
    [patientMedicalAlertsSubCategories],
  );

  const smokingDeviceOptions = useMemo(
    () => Utils.mapOptions(patientMedicalAlertsSubCategories?.smokingDevices),
    [patientMedicalAlertsSubCategories],
  );

  const smokingFrequencyTypesOptions = useMemo(
    () => Utils.mapOptions(patientMedicalAlertsSubCategories?.smokingFrequencyTypes),
    [patientMedicalAlertsSubCategories],
  );
  const alcoholStatusOptions = useMemo(
    () => Utils.mapOptions(patientMedicalAlertsSubCategories?.alcoholDrinkingStatuses),
    [patientMedicalAlertsSubCategories],
  );

  const substanceAbuseStatusOptions = useMemo(
    () => Utils.mapOptions(patientMedicalAlertsSubCategories?.substanceAbuseStatuses),
    [patientMedicalAlertsSubCategories],
  );

  const educationLevelStatusOptions = useMemo(
    () => Utils.mapOptions(patientMedicalAlertsSubCategories?.educationLevels),
    [patientMedicalAlertsSubCategories],
  );

  const employmentStatusOptions = useMemo(
    () => Utils.mapOptions(patientMedicalAlertsSubCategories?.employmentStatuses),
    [patientMedicalAlertsSubCategories],
  );

  const familyMembersOptions = useMemo(
    () => Utils.mapOptions(patientMedicalAlertsSubCategories?.familyMember),
    [patientMedicalAlertsSubCategories],
  );
  const switchValue = values[element?.switchName];

  const shouldDisableFrequencyTypesField = useMemo(() => {
    const disabledDevices = ["722496004", "722498003"];
    return disabledDevices.includes(values.smokingDevice?.code);
  }, [values.smokingDevice]);

  useEffect(() => {
    if (!element) return;

    const subType = (element?.subType || "").replace(/\s+/g, "_").toUpperCase();
    const resetField = (fieldName) => {
      dispatch(change("patientMedicalAlertsEdit", fieldName, null));
    };
    if (element.type === "_DISEASE_SOCIAL_HISTORY") {
      if (!values[element.switchName]) {
        const socialHistoryFields = {
          SMOKING: [
            "smokingStatus",
            "smokingDevice",
            "smokingFrequency",
            "smokingFrequencyType",
            "startSmokingAge",
            "stopSmokingAge",
          ],
          ALCOHOL: ["alcoholDrinkingStatus"],
          SUBSTANCE: ["substanceAbuseStatus"],
          EDUCATION: ["educationLevel"],
          EMPLOYMENT: ["employmentStatus"],
        };

        if (socialHistoryFields[subType]) {
          socialHistoryFields[subType].forEach(resetField);
        }
      }
    }
    if (element.type === "_DISEASE_FAMILY_HISTORY" && subType === "FAMILY_HISTORY") {
      if (!values[element.switchName]) {
        resetField("familyMember");
      }
    }
  }, [switchValue]);

  useEffect(() => {
    if (!values.smokingDevice) return;

    const frequencyTypes = {
      722496004: "# of Packs / Day",
      722498003: "mg of nicotine / Day",
    };
    const newFrequencyType = frequencyTypes[values.smokingDevice.code];
    if (newFrequencyType) {
      dispatch(
        change("patientMedicalAlertsEdit", "smokingFrequencyType", {
          code: newFrequencyType,
          name: newFrequencyType,
        }),
      );
    } else {
      dispatch(
        change("patientMedicalAlertsEdit", "smokingFrequencyType", {
          code: null,
          name: null,
        }),
      );
    }
  }, [values.smokingDevice]);

  const renderFieldElements = () => {
    const subType = (element?.subType || "").replace(/\s+/g, "").toUpperCase();
    if (element?.type === "_DISEASE_SOCIAL_HISTORY") {
      switch (subType) {
        case "SMOKING":
          return (
            <div className="d-flex flex-column pb-1" style={{ rowGap: 5 }}>
              <Field
                name={`smokingStatus-${element.switchName}`}
                id={`smokingStatus-${element.switchName}`}
                className="form-control"
                component={({ input }) => (
                  <SelectPicker
                    options={smokingStatusOptions}
                    placeholder="Smoking Status:"
                    isDisabled={!values[element.switchName]}
                    value={{
                      value: input.value?.code ? input.value.code : values?.smokingStatus?.code,
                    }}
                    onChange={(x) => input.onChange({ code: x.value, name: x.label })}
                  />
                )}
              />

              <Field
                name={`smokingDevice-${element.switchName}`}
                id={`smokingDevice-${element.switchName}`}
                component={({ input }) => (
                  <SelectPicker
                    options={smokingDeviceOptions}
                    placeholder="Smoking Device:"
                    isDisabled={!values[element.switchName]}
                    value={{
                      value: input.value?.code ? input.value.code : values?.smokingDevice?.code,
                    }}
                    onChange={(x) => input.onChange({ code: x.value, name: x.label })}
                  />
                )}
              />

              <Field
                name={`smokingFrequencyType-${element.switchName}`}
                id={`smokingFrequencyType-${element.switchName}`}
                component={({ input }) => (
                  <SelectPicker
                    options={smokingFrequencyTypesOptions}
                    placeholder="Smoking Frequency Types:"
                    isDisabled={!values[element.switchName] || shouldDisableFrequencyTypesField}
                    value={{
                      value: input.value?.code
                        ? input.value.code
                        : values?.smokingFrequencyType?.code,
                    }}
                    onChange={(x) => input.onChange({ code: x.value, name: x.label })}
                  />
                )}
              />

              <Field
                name={`smokingFrequency-${element.switchName}`}
                id={`smokingFrequency-${element.switchName}`}
                component="input"
                type="number"
                placeholder="Smoking Frequency:"
                className="form-control"
                disabled={!values[element.switchName]}
              />

              <Field
                name={`startSmokingAge-${element.switchName}`}
                id={`startSmokingAge-${element.switchName}`}
                component="input"
                type="number"
                placeholder="Start Smoking Age:"
                className="form-control"
                disabled={!values[element.switchName]}
                min={0}
              />

              <Field
                name={`stopSmokingAge-${element.switchName}`}
                id={`stopSmokingAge-${element.switchName}`}
                component="input"
                type="number"
                placeholder="Stop Smoking Age:"
                className="form-control"
                disabled={!values[element.switchName]}
                min={0}
              />
            </div>
          );

        case "ALCOHOL":
          return (
            <Field
              name="alcoholDrinkingStatus"
              id="alcoholDrinkingStatus"
              className="mt-1 mb-1"
              component={({ input }) => (
                <SelectPicker
                  placeholder="Alcohol Status:"
                  options={alcoholStatusOptions}
                  isDisabled={!values[element.switchName]}
                  value={{
                    value: input.value?.code
                      ? input.value.code
                      : values?.alcoholDrinkingStatus?.code,
                  }}
                  onChange={(x) => input.onChange({ code: x.value, name: x.label })}
                />
              )}
            />
          );

        case "SUBSTANCE":
          return (
            <Field
              name="substanceAbuseStatus"
              id="substanceAbuseStatus"
              className="mt-1 mb-1"
              component={({ input }) => (
                <SelectPicker
                  placeholder="Substance Abuse:"
                  options={substanceAbuseStatusOptions}
                  isDisabled={!values[element.switchName]}
                  value={{
                    value: input.value?.code
                      ? input.value.code
                      : values?.substanceAbuseStatus?.code,
                  }}
                  onChange={(x) => input.onChange({ code: x.value, name: x.label })}
                />
              )}
            />
          );

        case "EDUCATION":
          return (
            <Field
              name="educationLevel"
              id="educationLevel"
              className="mt-1 mb-1"
              component={({ input }) => (
                <SelectPicker
                  placeholder="Education Levels:"
                  options={educationLevelStatusOptions}
                  isDisabled={!values[element.switchName]}
                  value={{
                    value: input.value?.code ? input.value.code : values?.educationLevel?.code,
                  }}
                  onChange={(x) => input.onChange({ code: x.value, name: x.label })}
                />
              )}
            />
          );

        case "EMPLOYMENT":
          return (
            <Field
              name="employmentStatus"
              id="employmentStatus"
              className="mt-1 mb-1"
              component={({ input }) => (
                <SelectPicker
                  placeholder="Employment Status:"
                  options={employmentStatusOptions}
                  isDisabled={!values[element.switchName]}
                  value={{
                    value: input.value?.code ? input.value?.code : values?.employmentStatus?.code,
                  }}
                  onChange={(x) => input.onChange({ code: x.value, name: x.label })}
                />
              )}
            />
          );

        default:
          return (
            <Field
              component="input"
              type="text"
              name={element.noteName}
              className="form-control"
              disabled={!values[element.switchName]}
            />
          );
      }
    } else if (element?.type === "_DISEASE_FAMILY_HISTORY" && subType === "FAMILY_HISTORY") {
      return (
        <Field
          name="familyMember"
          id="familyMember"
          className="mt-1 mb-1"
          component={({ input }) => (
            <SelectPicker
              placeholder="Family Member:"
              options={familyMembersOptions}
              isDisabled={!values[element.switchName]}
              value={{ value: input.value?.code ? input.value.code : values?.familyMember?.code }}
              onChange={(x) => input.onChange({ code: x.value, name: x.label })}
            />
          )}
        />
      );
    } else {
      return (
        <Field component="input" type="text" name={element.noteName} className="form-control" />
      );
    }
  };

  return <>{renderFieldElements()}</>;
};

export default PatientMedicalAlertSubCategories;
